import { useEffect, useState } from "react";
import { type AppConfig, useConfig, useFacets } from "../lib/api";
import {
  activeFilterCount,
  DEFAULT_TIERS,
  DEFAULTS,
  type Search,
  type SearchPatch,
} from "../lib/search";
import {
  COOLING_TYPE_LABEL,
  FRAME_LABEL,
  SECTOR_LABEL,
  SEGMENT_LABEL,
  TIER_LABEL,
  TIER_ORDER,
  UNIT_STATUS_LABEL,
} from "../lib/tiers";
import { ChipSet } from "./controls/ChipSet";
import { DualRange } from "./controls/DualRange";
import { MultiPick } from "./controls/MultiPick";
import { Section } from "./controls/Section";

type Props = {
  search: Search;
  patch: (p: SearchPatch) => void;
  reset: () => void;
  /** map-only controls (basemap, satellite, outlines) */
  showMapControls?: boolean;
};

const COOLING_OPTIONS = Object.entries(COOLING_TYPE_LABEL).map(([value, label]) => ({
  value,
  label,
}));
const CONTACT_OPTIONS = [
  { value: "none", label: "None" },
  { value: "named", label: "Named" },
  { value: "verified", label: "Verified email" },
];

function statusOptions(cfg: AppConfig | undefined, mothballed: boolean) {
  const codes = [...(cfg?.statuses.default ?? ["OP", "SB", "OA"])];
  if (mothballed) codes.push(...(cfg?.statuses.mothballed ?? ["OS", "SC"]));
  return codes.map((c) => ({ value: c, label: c, title: UNIT_STATUS_LABEL[c] }));
}

export function FilterRail({ search, patch, reset, showMapControls = true }: Props) {
  const facets = useFacets();
  const config = useConfig();
  const cfg = config.data;
  const [operator, setOperator] = useState(search.operator ?? "");
  useEffect(() => setOperator(search.operator ?? ""), [search.operator]);

  const active = activeFilterCount(search);
  const segments = search.segment ?? ["A"];
  const pmOptions = [
    ...(segments.includes("A") || segments.includes("D")
      ? [
          { value: "GT", label: "GT", title: "Simple-cycle combustion turbine" },
          { value: "CT", label: "CT", title: "Combined-cycle combustion turbine" },
          { value: "CS", label: "CS", title: "Single-shaft combined cycle" },
        ]
      : []),
    ...(segments.includes("B")
      ? [{ value: "IC", label: "IC", title: "Reciprocating engine" }]
      : []),
  ];

  return (
    <aside
      className="scroll-thin flex w-[272px] shrink-0 flex-col gap-4 overflow-y-auto border-r p-3"
      style={{ borderColor: "var(--line)", background: "var(--panel)" }}
      aria-label="Filters"
    >
      <div className="flex items-center justify-between">
        <h1 className="eyebrow" style={{ color: "var(--paper)" }}>
          Filters{" "}
          {active > 0 ? (
            <span
              className="num rounded px-1"
              style={{ background: "var(--gold)", color: "var(--ink)" }}
            >
              {active}
            </span>
          ) : null}
        </h1>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={reset}
          disabled={active === 0}
        >
          Reset
        </button>
      </div>

      <Section
        title="Nameplate band"
        action={
          <label className="flex items-center gap-1 text-[11px]" style={{ color: "var(--muted)" }}>
            <input
              type="checkbox"
              checked={Boolean(search.core)}
              onChange={(e) =>
                patch(
                  e.target.checked
                    ? {
                        core: true,
                        mw_min: cfg?.band_core.mw_min ?? 40,
                        mw_max: cfg?.band_core.mw_max ?? 90,
                      }
                    : { core: undefined, mw_min: DEFAULTS.mw_min, mw_max: DEFAULTS.mw_max },
                )
              }
            />
            Core 40 to 90
          </label>
        }
        hint="Per unit, MW nameplate. Default 40 to 130 captures E-class frames and all aeroderivatives."
      >
        <DualRange
          min={0}
          max={300}
          step={5}
          unit="MW"
          label="Nameplate band"
          value={[search.mw_min, search.mw_max]}
          onChange={([lo, hi]) => patch({ mw_min: lo, mw_max: hi, core: undefined, page: 1 })}
        />
      </Section>

      <Section title="Segment">
        <ChipSet
          label="Segment"
          options={(["A", "B", "D"] as const).map((s) => ({
            value: s,
            label: SEGMENT_LABEL[s] ?? s,
          }))}
          value={search.segment}
          onChange={(v) => patch({ segment: v as Search["segment"], pm: undefined, page: 1 })}
        />
      </Section>

      <Section
        title="Climate preset"
        hint={
          facets.data && !facets.data.has_climate
            ? "No climate profile is published for the sites in view yet; only the Baseline band preset changes the result."
            : undefined
        }
      >
        <ChipSet
          label="Climate preset"
          options={(cfg?.presets ?? []).map((p) => ({ value: p.id, label: p.label }))}
          value={search.preset ? [search.preset] : undefined}
          onChange={(v) =>
            patch({ preset: (v?.[v.length - 1] as Search["preset"]) ?? undefined, page: 1 })
          }
        />
      </Section>

      <Section
        title="Score tier"
        hint={
          facets.data && !facets.data.has_scores
            ? "No scores are published for the sites in view yet."
            : search.tier === undefined
              ? "Default: tiers 1 and 2. Pick tiers to change it; select all four for every site."
              : undefined
        }
      >
        <ChipSet
          label="Tier"
          options={TIER_ORDER.map((t) => ({ value: t, label: TIER_LABEL[t] ?? t }))}
          value={search.tier ?? (facets.data?.has_scores ? DEFAULT_TIERS : undefined)}
          onChange={(v) => patch({ tier: v as Search["tier"], page: 1 })}
        />
      </Section>

      <Section title="Prime mover">
        <ChipSet
          label="Prime mover"
          options={pmOptions}
          value={search.pm}
          onChange={(v) => patch({ pm: v, page: 1 })}
        />
      </Section>

      <Section title="Frame class">
        <ChipSet
          label="Frame class"
          options={(facets.data?.frame_classes ?? []).map((f) => ({
            value: f.value,
            label: FRAME_LABEL[f.value] ?? f.value,
            count: f.count,
          }))}
          value={search.frame}
          onChange={(v) => patch({ frame: v, page: 1 })}
        />
      </Section>

      <Section
        title="Turbine OEM"
        hint={
          facets.data && facets.data.oems.length === 0
            ? "OEM and model come from enrichment (Phase 4)."
            : undefined
        }
      >
        <MultiPick
          label="Turbine OEM"
          options={(facets.data?.oems ?? []).map((o) => ({
            value: o.value,
            label: o.value,
            count: o.count,
          }))}
          value={search.oem}
          onChange={(v) => patch({ oem: v, page: 1 })}
          placeholder="GE Vernova, Siemens Energy…"
        />
        <label className="mt-2 flex items-center gap-2 text-[12px]">
          <input
            type="checkbox"
            checked={Boolean(search.bbm_ref)}
            onChange={(e) => patch({ bbm_ref: e.target.checked || undefined, page: 1 })}
          />
          BBM reference GT types only
        </label>
      </Section>

      <Section title="Existing inlet cooling">
        <ChipSet
          label="Inlet cooling"
          options={COOLING_OPTIONS}
          value={search.cooling}
          onChange={(v) => patch({ cooling: v, page: 1 })}
        />
      </Section>

      <Section title="Contact status">
        <ChipSet
          label="Contact status"
          options={CONTACT_OPTIONS}
          value={search.contact}
          onChange={(v) => patch({ contact: v as Search["contact"], page: 1 })}
        />
      </Section>

      <Section title="State">
        <MultiPick
          label="State"
          options={(facets.data?.states ?? []).map((s) => ({
            value: s.value,
            label: s.value,
            count: s.count,
          }))}
          value={search.state}
          onChange={(v) => patch({ state: v, page: 1 })}
          placeholder="TX, CA, AZ…"
        />
      </Section>

      <Section
        title="Balancing authority"
        hint="EIA balancing authority code (ERCO, CISO, SWPP, PJM, MISO…)."
      >
        <MultiPick
          label="Balancing authority"
          options={(facets.data?.balancing_authorities ?? []).map((b) => ({
            value: b.value,
            label: b.value,
            count: b.count,
          }))}
          value={search.ba}
          onChange={(v) => patch({ ba: v, page: 1 })}
          placeholder="ERCO, CISO…"
        />
      </Section>

      <Section title="Sector">
        <ChipSet
          label="Sector"
          options={Object.entries(SECTOR_LABEL).map(([value, label]) => ({ value, label }))}
          value={search.sector?.map(String)}
          onChange={(v) => patch({ sector: v?.map(Number), page: 1 })}
        />
      </Section>

      <Section title="Operator">
        <input
          className="input w-full"
          list="operator-options"
          value={operator}
          placeholder="Vistra, Calpine, LADWP…"
          aria-label="Operator name contains"
          onChange={(e) => setOperator(e.target.value)}
          onBlur={() => patch({ operator: operator.trim() || undefined, page: 1 })}
          onKeyDown={(e) =>
            e.key === "Enter" && patch({ operator: operator.trim() || undefined, page: 1 })
          }
        />
        <datalist id="operator-options">
          {(facets.data?.operators ?? []).map((o) => (
            <option key={o.value} value={o.value} />
          ))}
        </datalist>
      </Section>

      <Section title="Operating year">
        <DualRange
          min={1940}
          max={2035}
          label="Operating year"
          value={[search.year_min ?? 1940, search.year_max ?? 2035]}
          onChange={([lo, hi]) =>
            patch({
              year_min: lo > 1940 ? lo : undefined,
              year_max: hi < 2035 ? hi : undefined,
              page: 1,
            })
          }
        />
      </Section>

      <Section
        title="Unit status"
        action={
          <label className="flex items-center gap-1 text-[11px]" style={{ color: "var(--muted)" }}>
            <input
              type="checkbox"
              checked={Boolean(search.mothballed)}
              onChange={(e) =>
                patch({ mothballed: e.target.checked || undefined, status: undefined, page: 1 })
              }
            />
            Include mothballed
          </label>
        }
        hint="OP operating, SB standby, OA returned to service this year. Mothballed adds OS and SC."
      >
        <ChipSet
          label="Unit status"
          options={statusOptions(cfg, Boolean(search.mothballed))}
          value={search.status}
          onChange={(v) => patch({ status: v, page: 1 })}
        />
      </Section>

      {showMapControls ? (
        <Section
          title="Basemap"
          hint="Vector tiles from OpenFreeMap. Satellite: USGS NAIP (public domain) unless an ArcGIS key is configured."
        >
          <div className="flex flex-col gap-1 text-[12px]">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="basemap"
                checked={(search.basemap ?? "positron") === "positron"}
                onChange={() => patch({ basemap: undefined })}
              />
              Positron (light)
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="basemap"
                checked={search.basemap === "liberty"}
                onChange={() => patch({ basemap: "liberty" })}
              />
              Liberty (streets)
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={Boolean(search.sat)}
                onChange={(e) => patch({ sat: e.target.checked || undefined })}
              />
              Satellite imagery
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={search.osm !== false}
                onChange={(e) => patch({ osm: e.target.checked ? undefined : false })}
              />
              OSM plant outlines (zoom 13+)
            </label>
          </div>
        </Section>
      ) : null}
    </aside>
  );
}
